class ProductCustomizeModal extends HTMLElement {
  constructor() {
    super();

    this.canvasElement = this.querySelector('[data-customize-canvas]');
    this.closeButtons = this.querySelectorAll('[data-close-modal]');
    this.textToggleButtons = this.querySelectorAll('[data-text-toggle]');
    this.textInput = this.querySelector('[data-text-input]');
    this.textInputWrapper = this.querySelector('[data-text-input-wrapper]');
    this.textLengthDisplay = this.querySelector('[data-text-length]');
    this.clearButton = this.querySelector('[data-clear-btn]');
    this.applyButton = this.querySelector('[data-apply-btn]');
    this.loadingIndicator = this.querySelector('[data-canvas-loading]');

    this.modalPinSelector = this.querySelector('[data-modal-pin-selector]');
    this.modalPinButtons = this.querySelectorAll('[data-modal-pin-variant-id]');
    this.hasMokoPin = this.dataset.hasMokoPin === 'true';
    this.isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
    this.selectedMokoPinVariantId = null;
    this.pinFocalPoint = null;

    this.canvas = null;

    this.state = {
      pattern: null,
      color: null,
      text: {
        enabled: true,
        content: '',
        position: { x: 0.5, y: 0.35 },
        fontSize: 8,
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 600,
        color: '#000000'
      }
    };

    this.renderDebounceTimer = null;
    this.initialState = null;

    this.handleClose = this.handleClose.bind(this);
    this.handleTextToggle = this.handleTextToggle.bind(this);
    this.handleTextInput = this.handleTextInput.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handlePinSelect = this.handlePinSelect.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 250);
  }

  connectedCallback() {
    const baseImageUrl = this.dataset.productImageUrl;
    const textFontSize = parseInt(this.dataset.textFontSize) || 8;

    this.state.text.fontSize = textFontSize;

    // Re-query pin elements in connectedCallback to guarantee children are
    // available (constructor may fire before child elements are parsed).
    this.hasMokoPin = this.dataset.hasMokoPin === 'true';
    this.isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
    this.modalPinSelector = this.querySelector('[data-modal-pin-selector]');
    this.modalPinButtons = this.querySelectorAll('[data-modal-pin-variant-id]');

    if (this.canvasElement && baseImageUrl) {
      this.showLoading();
      try {
        this.canvas = new CustomizeCanvas(this.canvasElement, baseImageUrl);
        this.canvas.init().catch(() => {
          console.warn('[ProductCustomizeModal] Canvas initialization failed');
        });
      } catch (error) {
        console.warn('[ProductCustomizeModal] Failed to initialize canvas:', error);
      }

      setTimeout(() => {
        this.hideLoading();
      }, 500);
    }

    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', this.handleClose);
    });

    if (this.textInput) {
      this.textInput.addEventListener('input', this.handleTextInput);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.handleClear);
    }

    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.handleApply);
    }

    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.handleResize);

    if (this.hasMokoPin && this.modalPinSelector) {
      this.modalPinSelector.addEventListener('click', this.handlePinSelect);
      const initialPin = this.modalPinSelector.querySelector('[data-selected="true"]');
      if (initialPin) {
        this.selectedMokoPinVariantId = initialPin.dataset.modalPinVariantId;
        const focalX = parseFloat(initialPin.dataset.pinFocalX);
        const focalY = parseFloat(initialPin.dataset.pinFocalY);
        if (Number.isFinite(focalX) && Number.isFinite(focalY)) {
          this.pinFocalPoint = { x: focalX, y: focalY };
        }
      }
    }
  }

  disconnectedCallback() {
    this.closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleClose);
    });

    if (this.textInput) {
      this.textInput.removeEventListener('input', this.handleTextInput);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.handleClear);
    }

    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.handleApply);
    }

    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);

    if (this.modalPinSelector) {
      this.modalPinSelector.removeEventListener('click', this.handlePinSelect);
    }
  }

  open() {
    this.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
    this.classList.add('opacity-100', 'visible');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (!this.canvas && this.canvasElement) {
      const baseImageUrl = this.dataset.productImageUrl;
      if (baseImageUrl) {
        this.showLoading();
        try {
          this.canvas = new CustomizeCanvas(this.canvasElement, baseImageUrl);
          this.canvas.init().catch(() => {
            console.warn('[ProductCustomizeModal] Canvas initialization failed');
          });
        } catch (error) {
          console.warn('[ProductCustomizeModal] Failed to initialize canvas:', error);
        }
        setTimeout(() => {
          this.hideLoading();
        }, 500);
      }
    } else if (this.canvas) {
      setTimeout(() => {
        this.canvas.handleResize();
      }, 100);
    }

    const firstFocusable = this.querySelector('button:not([disabled]), [href], input:not([disabled])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }

    // Snapshot only on the first open. Overwriting on every open captures
    // unsaved typed text as the new baseline, leaving Apply disabled when the
    // user reopens the modal after closing without applying.
    if (this.initialState === null) {
      this.initialState = JSON.parse(JSON.stringify(this.state));
    }

    this.updateButtonStates();
  }

  close() {
    // Discard unsaved edits — revert to the last applied baseline so the
    // typed text doesn't linger on reopen. After handleApply this is a no-op
    // because initialState was just synced to the current state.
    if (this.initialState) {
      this.state = JSON.parse(JSON.stringify(this.initialState));

      if (this.textInput) {
        this.textInput.value = this.state.text.content || '';
      }

      if (this.textInputWrapper) {
        this.textInputWrapper.classList.toggle('hidden', !this.state.text.enabled);
      }

      if (this.canvas) {
        if (this.state.text.enabled && this.state.text.content) {
          this.canvas.setText(this.getCanvasTextState());
        } else {
          this.canvas.setText(null);
        }
      }

      this.updateTextLength();
      this.updateButtonStates();
    }

    this.classList.add('opacity-0', 'invisible', 'pointer-events-none');
    this.classList.remove('opacity-100', 'visible');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  handleClose() {
    this.close();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && !this.classList.contains('pointer-events-none')) {
      this.close();
    }
  }

  handlePinSelect(event) {
    const button = event.target.closest('[data-modal-pin-variant-id]');
    if (!button || button.disabled) return;

    this.modalPinButtons.forEach((btn) => {
      btn.removeAttribute('data-selected');
      btn.classList.remove('border-black', 'ring-1', 'ring-black');
      btn.classList.add('border-gray-200');
    });

    button.setAttribute('data-selected', 'true');
    button.classList.remove('border-gray-200');
    button.classList.add('border-black', 'ring-1', 'ring-black');

    this.selectedMokoPinVariantId = button.dataset.modalPinVariantId;

    const engravingUrl = button.dataset.pinEngravingUrl;
    const focalX = parseFloat(button.dataset.pinFocalX) || 0.5;
    const focalY = parseFloat(button.dataset.pinFocalY) || 0.5;

    this.pinFocalPoint = { x: focalX, y: focalY };

    if (engravingUrl && this.canvas) {
      this.showLoading();
      this.canvas.baseImageUrl = engravingUrl;
      this.canvas.loadBaseImage()
        .then(() => {
          if (this.state.text.content) {
            this.canvas.setText(this.getCanvasTextState());
          }
          this.hideLoading();
        })
        .catch(() => {
          this.hideLoading();
        });
    }

    if (this.isSelfMokoPin && this.selectedMokoPinVariantId) {
      this.switchParentVariant(this.selectedMokoPinVariantId);
    }
  }

  switchParentVariant(variantId) {
    const targetId = parseInt(variantId, 10);
    if (!Number.isFinite(targetId)) return;

    const variantSwitcher = document.querySelector('product-variant-switcher');
    if (!variantSwitcher) return;

    const productData = variantSwitcher.productData;
    const variant = productData?.variants?.find((v) => v.id === targetId);
    if (!variant) return;

    if (variantSwitcher.currentVariantId === targetId) return;

    variantSwitcher.currentVariantId = targetId;
    variantSwitcher.updateSwatchesState?.(targetId);
    variantSwitcher.updateMediaVisibility?.(targetId);
    variantSwitcher.updatePrice?.(variant);
    variantSwitcher.updateForm?.(variant);
    variantSwitcher.updateVariantTitle?.(variant);
    variantSwitcher.updateURL?.(targetId);

    document.dispatchEvent(
      new CustomEvent('variant:changed', {
        detail: { variantId: targetId, variant },
        bubbles: true,
      })
    );
  }

  setSelectedMokoPinVariant(variantId) {
    if (!this.hasMokoPin || !variantId) return;
    const button = this.modalPinSelector?.querySelector(
      `[data-modal-pin-variant-id="${variantId}"]`
    );
    if (button && !button.disabled) {
      this.handlePinSelect({ target: button });
    }
  }

  handleTextToggle(event) {
    const button = event.currentTarget;
    const toggleType = button.dataset.textToggle;

    this.textToggleButtons.forEach(btn => {
      btn.classList.remove('bg-black', 'text-white', 'border-black');
      btn.classList.add('border-gray-300', 'text-gray-600');
    });

    if (toggleType === 'add') {
      button.classList.remove('border-gray-300', 'text-gray-600');
      button.classList.add('bg-black', 'text-white', 'border-black');
      this.state.text.enabled = true;
      if (this.textInputWrapper) {
        this.textInputWrapper.classList.remove('hidden');
        if (this.textInput) {
          this.textInput.focus();
        }
      }
      return;
    }
    button.classList.remove('border-gray-300', 'text-gray-600');
    button.classList.add('border-black', 'text-black');
    this.state.text.enabled = false;
    this.state.text.content = '';
    if (this.textInputWrapper) {
      this.textInputWrapper.classList.add('hidden');
    }
    if (this.textInput) {
      this.textInput.value = '';
    }
    if (this.canvas) {
      this.canvas.setText(null);
    }
    this.updateTextLength();
  }

  handleTextInput(event) {
    let text = event.target.value;
    const validationError = this.querySelector('[data-validation-error]');

    const alphanumericRegex = /^[A-Za-z0-9.]*$/;
    const isValid = alphanumericRegex.test(text);

    if (!isValid) {
      text = text.replace(/[^A-Za-z0-9.]/g, '');

      if (validationError) {
        validationError.classList.remove('hidden');
        setTimeout(() => {
          validationError.classList.add('hidden');
        }, 2000);
      }
    }

    text = text.toUpperCase();
    event.target.value = text;

    this.state.text.content = text;
    this.updateTextLength();

    this.updateButtonStates();

    if (this.canvas && this.state.text.enabled) {
      this.debouncedCanvasUpdate();
    }
  }

  updateTextLength() {
    if (this.textLengthDisplay && this.textInput) {
      this.textLengthDisplay.textContent = this.textInput.value.length;
    }
  }

  /**
   * Returns text state adjusted for Moko Pin mode:
   * - Position overridden with the selected pin's focal point (immune
   *   to overwrites from loadSavedCustomization which restores the
   *   product engraving position).
   * - Font size: add-ons use font_size from the personalisation_addon registry,
   *   read straight off the dataset because state.text.fontSize is mutable and
   *   gets overwritten by updateEngravingData() and by loadSavedCustomization()
   *   restoring older localStorage. Plain engraving uses the font_size from the
   *   engravings metaobject (data-engraving-font-size) when the product has one,
   *   otherwise the desktop/mobile defaults below.
   */
  getCanvasTextState() {
    const BASELINE_CANVAS_DIM = 400;
    const ENGRAVING_FONT_SIZE_DESKTOP = 6;
    const ENGRAVING_FONT_SIZE_MOBILE = 8;
    const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

    const registryFontSize = parseFloat(this.dataset.addonFontSize);
    const hasRegistryFontSize = Number.isFinite(registryFontSize) && registryFontSize > 0;

    const metaobjectFontSize = parseFloat(this.dataset.engravingFontSize);
    const hasMetaobjectFontSize = Number.isFinite(metaobjectFontSize) && metaobjectFontSize > 0;

    const defaultEngravingFontSize = window.matchMedia(DESKTOP_MEDIA_QUERY).matches
      ? ENGRAVING_FONT_SIZE_DESKTOP
      : ENGRAVING_FONT_SIZE_MOBILE;

    const engravingFontSize = hasMetaobjectFontSize ? metaobjectFontSize : defaultEngravingFontSize;

    const baseFontSize = hasRegistryFontSize ? registryFontSize : engravingFontSize;

    let fontSize = baseFontSize;

    const canvasEl = this.canvas?.canvas;
    if (canvasEl) {
      const dpr = window.devicePixelRatio || 1;
      const canvasDim = Math.min(canvasEl.width / dpr, canvasEl.height / dpr);
      if (canvasDim > 0) {
        fontSize = baseFontSize * (canvasDim / BASELINE_CANVAS_DIM);
      }
    }

    if (this.hasMokoPin) {
      return {
        ...this.state.text,
        fontSize,
        position: this.pinFocalPoint ?? this.state.text.position,
      };
    }

    return {
      ...this.state.text,
      fontSize,
    };
  }

  debouncedCanvasUpdate() {
    clearTimeout(this.renderDebounceTimer);
    this.renderDebounceTimer = setTimeout(() => {
      if (this.canvas && this.state.text.enabled && this.state.text.content) {
        this.canvas.setText(this.getCanvasTextState());
      } else if (this.canvas) {
        this.canvas.setText(null);
      }
    }, 150);
  }

  hasStateChanged() {
    if (!this.initialState) return false;

    const currentText = this.state.text.content.trim();
    const initialText = this.initialState.text.content.trim();

    return currentText !== initialText;
  }

  hasTextInput() {
    return this.state.text.content.trim().length > 0;
  }

  updateButtonStates() {
    const hasText = this.hasTextInput();
    const hasChanged = this.hasStateChanged();

    if (this.clearButton) {
      if (hasText) {
        this.clearButton.classList.remove('opacity-50', 'cursor-not-allowed');
        this.clearButton.removeAttribute('disabled');
      } else {
        this.clearButton.classList.add('opacity-50', 'cursor-not-allowed');
        this.clearButton.setAttribute('disabled', 'true');
      }
    }

    if (this.applyButton) {
      if (hasChanged) {
        this.applyButton.classList.remove('opacity-50', 'cursor-not-allowed');
        this.applyButton.removeAttribute('disabled');
      } else {
        this.applyButton.classList.add('opacity-50', 'cursor-not-allowed');
        this.applyButton.setAttribute('disabled', 'true');
      }
    }
  }

  snapshotInitialState() {
    this.initialState = JSON.parse(JSON.stringify(this.state));
    this.updateButtonStates();
  }

  resetToNewDesign() {
    const currentPosition = { ...this.state.text.position };

    this.state = {
      pattern: null,
      color: null,
      text: {
        enabled: true,
        content: '',
        position: currentPosition,
        fontSize: this.state.text.fontSize || 8,
        fontFamily: this.state.text.fontFamily || 'Montserrat, sans-serif',
        fontWeight: this.state.text.fontWeight || 600,
        color: this.state.text.color || '#000000'
      }
    };

    if (this.textInput) {
      this.textInput.value = '';
    }

    if (this.canvas) {
      this.canvas.clear();
    }

    this.updateTextLength();
  }

  handleClear() {
    const currentPosition = { ...this.state.text.position };

    this.state = {
      pattern: null,
      color: null,
      text: {
        enabled: true,
        content: '',
        position: currentPosition,
        fontSize: this.state.text.fontSize || 8,
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 600,
        color: '#000000'
      }
    };

    if (this.textInput) {
      this.textInput.value = '';
    }

    if (this.canvas) {
      this.canvas.clear();
    }

    this.updateTextLength();

    this.updateButtonStates();
  }

  handleApply() {
    if (!this.hasStateChanged()) {
      return;
    }

    const customizationData = this.getCustomizationData();

    let previewImage = null;
    if (this.canvas && this.canvas.canvas) {
      try {
        const canvasElement = this.canvas.canvas;
        if (canvasElement && typeof canvasElement.toDataURL === 'function') {
          previewImage = canvasElement.toDataURL('image/png');
        }
      } catch (e) {
        console.error('[ProductCustomize] Failed to capture preview image:', e);
      }
    }

    if (previewImage) {
      customizationData.previewImage = previewImage;
    }

    if ((this.hasMokoPin || this.isSelfMokoPin) && this.selectedMokoPinVariantId) {
      customizationData.selectedMokoPinVariantId = this.selectedMokoPinVariantId;
    }

    const event = new CustomEvent('customization:applied', {
      detail: customizationData,
      bubbles: true
    });
    this.dispatchEvent(event);

    this.initialState = JSON.parse(JSON.stringify(this.state));

    this.close();
  }

  updateEngravingData(imageUrl, position, fontSize, price, priceFormatted, comparePrice, comparePriceFormatted) {
    if (!imageUrl || !this.canvas) return;

    this.showLoading();

    if (position) {
      this.state.text.position = {
        x: position.x || 0.6,
        y: position.y || 0.41
      };
    }

    if (fontSize) {
      this.state.text.fontSize = parseInt(fontSize) || 8;
    }

    this.dataset.productImageUrl = imageUrl;

    if (price !== undefined) {
      this.dataset.productPrice = price;
    }
    if (priceFormatted) {
      this.dataset.productPriceFormatted = priceFormatted;
    }
    if (comparePrice !== undefined) {
      this.dataset.productComparePrice = comparePrice;
    }
    if (comparePriceFormatted) {
      this.dataset.productComparePriceFormatted = comparePriceFormatted;
    }

    this.updatePriceDisplay();

    this.canvas.baseImageUrl = imageUrl;
    this.canvas.loadBaseImage()
      .then(() => {
        this.canvas.setText(this.getCanvasTextState());
        this.hideLoading();

        this.initialState = JSON.parse(JSON.stringify(this.state));
        this.updateButtonStates();
      })
      .catch(error => {
        console.error('[ProductCustomizeModal] Failed to update engraving image:', error);
        this.hideLoading();
      });
  }

  updatePriceDisplay() {
    const priceDisplay = this.querySelector('[data-product-price-display]');
    const comparePriceDisplay = this.querySelector('[data-product-compare-price-display]');

    if (priceDisplay && this.dataset.productPriceFormatted) {
      priceDisplay.textContent = this.dataset.productPriceFormatted;
    }

    if (!comparePriceDisplay) {
      return;
    }
    const productPrice = parseInt(this.dataset.productPrice) || 0;
    const comparePrice = parseInt(this.dataset.productComparePrice) || 0;

    if (comparePrice > productPrice && this.dataset.productComparePriceFormatted) {
      comparePriceDisplay.textContent = this.dataset.productComparePriceFormatted;
      comparePriceDisplay.parentElement.classList.remove('hidden');
    } else {
      comparePriceDisplay.parentElement.classList.add('hidden');
    }
  }

  getCustomizationData() {
    const data = {
      pattern: this.state.pattern ? {
        id: this.state.pattern.id,
        name: this.state.pattern.name
      } : null,
      color: this.state.color ? {
        id: this.state.color.id,
        name: this.state.color.name,
        hex: this.state.color.hex
      } : null,
      text: this.state.text.enabled && this.state.text.content ? {
        content: this.state.text.content,
        position: this.state.text.position,
        fontSize: this.state.text.fontSize,
        fontFamily: this.state.text.fontFamily,
        color: this.state.text.color
      } : null
    };

    return data;
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('hidden');
    }
  }

  handleResize() {
    if (this.canvas) {
      try {
        this.canvas.handleResize();
        if (this.state.text.enabled && this.state.text.content) {
          this.canvas.setText(this.getCanvasTextState());
        }
      } catch (error) {
        console.error('[ProductCustomizeModal] Error handling resize:', error);
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (!window.customElements.get('product-customize-modal')) {
  window.customElements.define('product-customize-modal', ProductCustomizeModal);
}

window.openProductCustomizeModal = (productId) => {
  const modal = document.getElementById(`product-customize-modal-${productId}`);
  if (modal) {
    modal.open();
  }
};

